import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import type { Booking } from "@/types";
import { BottomNav } from "@/components/navigation/BottomNav";
import { toast } from "sonner";
import { invokeFunctionWithAuth } from "@/lib/supabase/functions";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function BookingHistoryPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBookings = async () => {
      // Fetch via edge function with JWT
      if (user) {
        try {
          const { data, error } = await invokeFunctionWithAuth('get-customer-bookings', {
            phone: user.phone,
            userId: user.id
          });

          if (error) {
            console.error('❌ Error from get-customer-bookings:', error);
            throw error;
          }
          
          // Transform the data to match the Booking type
          const transformedBookings: Booking[] = (data.bookings || []).map((booking: any) => ({
            ...booking,
            filmmaker: booking.filmmaker ? {
              id: booking.filmmaker.id,
              bio: booking.filmmaker.bio,
              rating: booking.filmmaker.rating,
              full_name: booking.filmmaker.profiles?.full_name || 'Filmmaker',
              phone: booking.filmmaker.profiles?.phone || '',
              email: booking.filmmaker.profiles?.email || '',
              role: 'filmmaker' as const,
            } : undefined
          }));
          
          setBookings(transformedBookings);
        } catch (error: any) {
          console.error('Error loading bookings:', error);
          toast.error('Failed to load bookings');
        }
      }
      setLoading(false);
    };

    loadBookings();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const historyBookings = bookings.filter(b => 
    ['completed', 'canceled'].includes(b.status)
  );

  const userDisplayName = user?.fullName || user?.phone || 'User';
  const userAvatarUrl = user?.avatar_url;

  return (
    <div className="min-h-screen bg-white pb-24">
      <header className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-[#E1E3E6]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="h-10 w-10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">Your Past Shootings</h1>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-10 w-10 rounded-full p-0">
                {userAvatarUrl ? (
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={userAvatarUrl} alt={userDisplayName} />
                    <AvatarFallback className="bg-gray-100 text-gray-600">
                      {userDisplayName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-gray-100 text-gray-600">
                      {userDisplayName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="px-5 pt-6 max-w-screen-md mx-auto">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading past shootings...</p>
          </div>
        ) : historyBookings.length === 0 ? (
          <Card className="p-8 text-center">
            <h3 className="font-semibold text-lg mb-2">No past shootings</h3>
            <p className="text-sm text-muted-foreground">
              You haven't completed any shootings yet.
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {historyBookings.map((booking) => {
              const bookingId = 'BK-' + booking.id.substring(0, 8).toUpperCase();
              const formatDate = (dateStr: string) => {
                const date = new Date(dateStr);
                const day = date.getDate();
                const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                               day === 2 || day === 22 ? 'nd' : 
                               day === 3 || day === 23 ? 'rd' : 'th';
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                const year = date.getFullYear();
                return `${month} ${day}${suffix}, ${year}`;
              };
              
              return (
                <div key={booking.id}>
                  <Link to={`/track/${bookingId}`}>
                    <Card className="p-0 hover:border-primary transition-colors flex">
                      <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src="https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&h=400&fit=crop" 
                          alt={booking.package?.name || 'Booking'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 p-4">
                        <h3 className="font-bold text-base mb-1">{booking.package?.name || 'The Glow Session'}</h3>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(booking.scheduled_at)} • ${(booking.total_cents / 100).toFixed(2)}
                        </p>
                      </div>
                    </Card>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

